// Render-side MIRROR of common-lib `stripLeakedFraming` (which runs persist-side in the harness's
// gateway ingestion). The harness now strips its own in-band framing before a row is stored, so this is
// the catch-all for legacy history and the live-stream flash — the same role the `<voice>` strip plays.
// Kept 1:1 with the server, iOS `ChatText.stripLeakedFraming`, and Android `ChatText.stripLeakedFraming`;
// the shared `common-lib/fixtures/chat-render-contract.json` guards the three clients against drift.
//
// Not imported directly from common-lib on purpose: that module (`gatewayMapping`) pulls in server-side
// mapping code we don't want in the web bundle, and each client already keeps its own copy of these
// render-time strips.

// `<metadata>…</metadata>` / `<system>…</system>` / `<thinking>…</thinking>` block — in-band framing the
// model can write into its reply. The first two are ours and reach the model as tagged text inside a turn,
// so anything it reads it can echo (or, for `<system>`, fabricate — a real turn opened with an invented
// `<system>[AGENT REPORT, SUCCESS] …`). `<thinking>` is never ours: it is the model reproducing the shape
// of the real reasoning blocks its own earlier turns produced, once the request stopped enabling thinking.
// Non-greedy, with a back-referenced close tag so the block ends at its OWN closer.
//
// The second alternative is the LOOK-ALIKE `‹metadata›…‹/metadata›` (U+2039 / U+203A). The harness
// neutralises the model's own earlier hand-written `<metadata>` blocks into that shape before it shows
// them back (so a reply cannot forge real framing), the model copies what it reads, and its next reply
// ends in the look-alike — which no strip matched, so the choices JSON showed raw on every surface (C37).
// Scoped to `metadata`, the shape seen leaking (the harness neutralises `<system>` and others the same way;
// widen this only on evidence). Same scope and anchoring as the real tag, nothing wider. The persist-side
// strip learns the look-alike in the harness half of C37; until then this render-side copy is ahead of it.
const FRAMING_BLOCK =
  /[ \t]*(?:<(metadata|system|thinking)>[\s\S]*?<\/\1>|\u2039metadata\u203A[\s\S]*?\u2039\/metadata\u203A)[ \t]*/g;

// The look-alike opener, as an alternative wherever the real openers are accepted below.
const LOOKALIKE_METADATA_OPEN = '\u2039metadata\u203A';

// The same wrapper at the HEAD of the reply, closed with the wrong tag or not closed at all. An opener
// with nothing closing it is a truncated reply, so everything after it goes too — which is what makes
// this the settled counterpart of the streaming strip below, rather than a rule only a live partial gets.
// Mirrors common-lib `FRAMING_MANGLED_HEAD`; `^`-anchored with no `m` flag, so a wrapper further down the
// reply is the next rule's business and a mid-paragraph mention is nobody's.
const FRAMING_MANGLED_HEAD = /^[ \t]*(?:<(?:metadata|system|thinking)>|\u2039metadata\u203A)[\s\S]*?(?:<\/[a-zA-Z][\w-]*>|$)[ \t]*/;

// The same wrapper closed with the WRONG tag, occupying the rest of its own line — `<metadata>{…}</parameter>`,
// a tool-call frame's closer bleeding into the text. `FRAMING_BLOCK`'s backreference needs a matching closer,
// so these survived it and are the shape stored history actually carries: one row opened a turn with a
// mis-closed `<system>` nudge, another answered `הנה!` and then suffixed the attachment metadata it had been
// reading on its own prior replies. Start-of-line only (head or mid-reply), and confined to that ONE line, so
// it can neither take a mid-paragraph mention nor cross into the answer to reach a `</div>` the reply quotes.
// A closer is required HERE: past the head, every line after an opener is text the user was meant to read,
// so an opener with nothing closing it stays — the head rule above is the only place a missing closer
// swallows the rest.
const FRAMING_MANGLED_LINE = /(?:^|\n)[ \t]*(?:<(?:metadata|system|thinking)>|\u2039metadata\u203A)[^\n]*?<\/[a-zA-Z][\w-]*>[ \t]*/g;

// A bracketed turn marker on a line of ITS OWN — `[AGENT REPORT …]`, `[TASK COMPLETED …]`, etc. Anchored
// to a whole line (the `m` flag) so a sentence that merely mentions one mid-paragraph is left alone.
const FRAMING_MARKER_LINE =
  /^[ \t]*\[(?:AGENT REPORT|TASK COMPLETED|TASK BLOCKED|TASK RESUMED|SUB-AGENT|NUDGE FROM|sub-agent conversation_id|run cost)\b[^\]\n]*\][ \t]*$\n?/gim;

// Strip the platform's own in-band framing out of model-emitted text. Removes the `<metadata>`/`<system>`
// blocks AND their content (unlike `stripVoiceTags`, which keeps the inner words), plus the bracketed
// turn-marker lines, collapses the blank runs a removal leaves, and trims. Deliberately narrow — only the
// exact wrappers we inject — so it can't eat a user-facing sentence; idempotent, and text with no framing
// returns unchanged (the fast-path guard).
export function stripLeakedFraming(text: string): string {
  if (
    !text.includes('<metadata>') &&
    !text.includes('<system>') &&
    !text.includes('<thinking>') &&
    !text.includes(LOOKALIKE_METADATA_OPEN) &&
    !text.includes('[')
  ) {
    return text;
  }
  return text
    .replace(FRAMING_BLOCK, '')
    .replace(FRAMING_MANGLED_HEAD, '')
    .replace(FRAMING_MANGLED_LINE, '')
    .replace(FRAMING_MARKER_LINE, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// An UNCLOSED trailing framing open tag: a lowercase `<metadata>`/`<system>` with no matching close
// (case-sensitive, like the block).
const UNCLOSED_FRAMING_TAIL = /(?:<(?:metadata|system|thinking)>|\u2039metadata\u203A)[\s\S]*$/;

// STREAMING-ONLY strip. After the balanced blocks are gone (`stripLeakedFraming`), a live reply can still
// hold an OPENING `<system>`/`<metadata>` whose closing tag hasn't streamed yet — the fabricated
// `<system>[AGENT REPORT …]` opening a turn. Remove that unclosed tag and everything after it so the
// partial never flashes; once the close arrives the balanced strip handles it identically. Apply ONLY on
// the streaming render path, never to settled text — so a genuinely-unclosed `<system>` in stored history
// keeps exact server parity. Mirrors iOS/Android `stripStreamingPartialFraming`.
export function stripStreamingLeakedFraming(text: string): string {
  return stripLeakedFraming(text).replace(UNCLOSED_FRAMING_TAIL, '').replace(/[ \t\n]+$/, '');
}
